// CDMLibrary.jsx  (React CLASS component + Tailwind)
// 2 tabs: Collection (CDMUser) + Library (CDMLibrary)
//
// Backend routes (withCredentials:true):
// Collection (private):
//   GET    /cdmUser/list
//   POST   /cdmUser/create
//   PATCH  /cdmUser/:id
//   DELETE /cdmUser/:id
//   POST   /cdmUser/:id/publish
//
// Library (public):
//   GET    /cdmLibrary/list
//   POST   /cdmLibrary/:id/like
//   POST   /cdmLibrary/:id/copyToUser
//
// NOTE:
// - Your backend JWT stores user id at req.userData.userId (server-side).
// - This client restores add/edit/delete for goals/todos/referrals/comments by PATCH-ing the whole array on CDMUser.

import React from "react";
import axios from "axios";
import { useAuth } from "../../utilities/AuthContext";
import { useNavigation } from "../../utilities/NavigationContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { showSuccessToast, showErrorToast } from "../../utilities/helper";

import { HandThumbUpIcon as HandThumbUpOutline } from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolid } from "@heroicons/react/24/solid";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

// If your server mounts under /api, change these to `${EndPoint}/api/cdmUser` etc.
const CDM_USER = `${EndPoint}/cdmUser`;
const CDM_LIBRARY = `${EndPoint}/cdmLibrary`;

class CDMLibrary extends React.Component {
  state = {
    activeTab: "collection", // collection | library

    // Current user display name (for new entries)
    author: "",

    // Collection (private)
    collection: [],
    collectionLoading: false,

    // Public library
    library: [],
    libraryLoading: false,

    // UI
    searchCollection: "",
    searchLibrary: "",
    filterCategory: "All",
    selectedCategory: "Chronic Disease",

    // Add/Edit template (title/category)
    newTitle: "",
    editingId: null,
    editingTitle: "",
    editingCategory: "",

    // Entry editing (goals/todos/referrals/comments)
    entryInputs: {}, // { `${templateId}_${type}`: "text" }  type: goal|todo|referral|comment
    editingEntries: {}, // { [entryId]: "text" }

    // Like state (local map: libraryTemplateId -> boolean)
    likedMap: {},

    // Optional: collapse/expand per template
    collapsed: {}, // { [templateId]: boolean }
  };

  categoryOptions = [
    { label: "Chronic Disease", color: "bg-sky-200" },
    { label: "Mental Health", color: "bg-purple-200" },
    { label: "Lifestyle", color: "bg-green-200" },
    { label: "Substance Use", color: "bg-red-200" },
    { label: "Preventive", color: "bg-orange-200" },
    { label: "Senior Health", color: "bg-slate-300" },
    { label: "Social", color: "bg-yellow-200" },
  ];

  componentDidMount() {
    this.fetchUserData();
    this.loadCollection();
    this.loadLibrary();
  }

  // ---------------------------
  // User
  // ---------------------------
  fetchUserData = async () => {
    try {
      const res = await axios.get(`${EndPoint}/user/user_detail`, {
        withCredentials: true,
      });
      const author = `${res.data.firstName || ""} ${res.data.lastName || ""}`.trim();
      this.setState({ author });
    } catch (err) {
      console.error("user_detail error:", err?.response?.data || err.message);
      // not fatal
    }
  };


  setTab = (tab) => this.setState({ activeTab: tab });

  toggleCollapse = (id) => {
    this.setState((prev) => ({
      collapsed: { ...prev.collapsed, [id]: !prev.collapsed[id] },
    }));
  };

  // ---------------------------
  // Collection (CDMUser)
  // ---------------------------
  loadCollection = async () => {
    try {
      this.setState({ collectionLoading: true });
      const res = await axios.get(`${CDM_USER}/list`, { withCredentials: true });
      const items = res.data.items || [];
      const collapsed = {};
      items.forEach((x) => (collapsed[x._id] = true));
      this.setState({ collection: items, collapsed });
    } catch (err) {
      console.error("CDMUser/list error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to load your collection.");
    } finally {
      this.setState({ collectionLoading: false });
    }
  };

  addToCollection = async () => {
    const { newTitle, selectedCategory } = this.state;
    if (!newTitle.trim()) return;

    try {
      const payload = {
        title: newTitle.trim(),
        category: selectedCategory,
        goals: [],
        todos: [],
        comments: [],
        referrals: [],
      };

      const res = await axios.post(`${CDM_USER}/create`, payload, {
        withCredentials: true,
      });

      const item = res.data.item;

      this.setState((prev) => ({
        newTitle: "",
        collection: [item, ...prev.collection],
        collapsed: { ...prev.collapsed, [item._id]: true },
      }));

      showSuccessToast("Added to your collection.");
    } catch (err) {
      console.error("CDMUser/create error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to add.");
    }
  };

  startEditTemplate = (item) => {
    this.setState({
      editingId: item._id,
      editingTitle: item.title || "",
      editingCategory: item.category || this.state.selectedCategory,
    });
  };

  cancelEditTemplate = () => {
    this.setState({ editingId: null, editingTitle: "", editingCategory: "" });
  };

  updateCollectionItem = async (id) => {
    const { editingTitle, editingCategory } = this.state;
    if (!editingTitle.trim()) return;

    try {
      const updates = { title: editingTitle.trim(), category: (editingCategory || "").trim() };
      const res = await axios.patch(`${CDM_USER}/${id}`, updates, { withCredentials: true });
      const updated = res.data.item;

      this.setState((prev) => ({
        editingId: null,
        editingTitle: "",
        editingCategory: "",
        collection: prev.collection.map((x) => (x._id === id ? updated : x)),
      }));

      showSuccessToast("Updated.");
    } catch (err) {
      console.error("CDMUser/update error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to update.");
    }
  };

  deleteCollectionItem = async (id) => {
    try {
      await axios.delete(`${CDM_USER}/${id}`, { withCredentials: true });
      this.setState((prev) => ({
        collection: prev.collection.filter((x) => x._id !== id),
      }));
      showSuccessToast("Deleted.");
    } catch (err) {
      console.error("CDMUser/delete error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to delete.");
    }
  };

  publishCollectionItem = async (id) => {
    try {
      const res = await axios.post(
        `${EndPoint}/cdmUser/${id}/publish`,
        {},
        { withCredentials: true }
      );

      this.setState((prev) => ({
        collection: prev.collection.map((x) =>
          x._id === id ? { ...x, isPublic: true } : x
        ),
      }));

      showSuccessToast(res.data?.message || "Published to public library.");
      this.loadLibrary();
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to publish.");
    }
  };


  // ---------------------------
  // Entry-level CRUD (Collection only)
  // Implemented by PATCH-ing whole arrays to /cdmUser/:id
  // ---------------------------
  entryKey = (templateId, type) => `${templateId}_${type}`; // type: goal|todo|referral|comment
  fieldName = (type) => `${type}s`; // goals/todos/referrals/comments

  handleEntryInputChange = (templateId, type, value) => {
    const key = this.entryKey(templateId, type);
    this.setState((prev) => ({
      entryInputs: { ...prev.entryInputs, [key]: value },
    }));
  };

  startEditEntry = (entryId, text) => {
    this.setState((prev) => ({
      editingEntries: { ...prev.editingEntries, [entryId]: text },
    }));
  };

  cancelEditEntry = (entryId) => {
    this.setState((prev) => {
      const next = { ...prev.editingEntries };
      delete next[entryId];
      return { editingEntries: next };
    });
  };

  patchTemplateArray = async (templateId, field, nextArray) => {
    const res = await axios.patch(
      `${CDM_USER}/${templateId}`,
      { [field]: nextArray },
      { withCredentials: true }
    );
    return res.data.item;
  };

  addEntryToTemplate = async (templateId, type, event) => {
    if (event.key !== "Enter") return;

    const key = this.entryKey(templateId, type);
    const text = (this.state.entryInputs[key] || "").trim();
    if (!text) return;

    const field = this.fieldName(type);

    try {
      const template = this.state.collection.find((x) => x._id === templateId);
      if (!template) return;

      const newEntry = {
        entry: text,
        type, // schema requires enum
        author: this.state.author || "",
        dateAdded: new Date().toISOString(),
      };

      const nextArray = [ ...(template[field] || []), newEntry ];
      const updated = await this.patchTemplateArray(templateId, field, nextArray);

      this.setState((prev) => ({
        entryInputs: { ...prev.entryInputs, [key]: "" },
        collection: prev.collection.map((x) => (x._id === templateId ? updated : x)),
      }));

      showSuccessToast(`${type} added.`);
    } catch (err) {
      console.error("addEntry error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || `Failed to add ${type}.`);
    }
  };

  deleteEntryFromTemplate = async (templateId, type, entryId) => {
    const field = this.fieldName(type);

    try {
      const template = this.state.collection.find((x) => x._id === templateId);
      if (!template) return;

      const nextArray = (template[field] || []).filter((e) => String(e._id) !== String(entryId));
      const updated = await this.patchTemplateArray(templateId, field, nextArray);

      this.setState((prev) => ({
        collection: prev.collection.map((x) => (x._id === templateId ? updated : x)),
      }));

      showSuccessToast(`${type} deleted.`);
    } catch (err) {
      console.error("deleteEntry error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || `Failed to delete ${type}.`);
    }
  };

  saveEditedEntry = async (templateId, type, entryId, event) => {
    if (event.key !== "Enter") return;

    const newText = (this.state.editingEntries[entryId] || "").trim();
    if (!newText) return;

    const field = this.fieldName(type);

    try {
      const template = this.state.collection.find((x) => x._id === templateId);
      if (!template) return;

      const nextArray = (template[field] || []).map((e) =>
        String(e._id) === String(entryId) ? { ...e, entry: newText } : e
      );

      const updated = await this.patchTemplateArray(templateId, field, nextArray);

      this.setState((prev) => {
        const nextEditing = { ...prev.editingEntries };
        delete nextEditing[entryId];
        return {
          editingEntries: nextEditing,
          collection: prev.collection.map((x) => (x._id === templateId ? updated : x)),
        };
      });

      showSuccessToast(`${type} updated.`);
    } catch (err) {
      console.error("saveEditedEntry error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || `Failed to update ${type}.`);
    }
  };

  // ---------------------------
  // Library (CDMLibrary)
  // ---------------------------
  loadLibrary = async () => {
    try {
      this.setState({ libraryLoading: true });
      const res = await axios.get(`${CDM_LIBRARY}/list?sort=recent&limit=50`, {
        withCredentials: true,
      });
      const items = res.data.items || [];

      // default: collapsed = true for each library item
      const collapsed = { ...this.state.collapsed };
      items.forEach((x) => {
        if (collapsed[x._id] === undefined) collapsed[x._id] = true;
      });

      this.setState({ library: items, collapsed });
    } catch (err) {
      console.error("CDMLibrary/list error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to load public library.");
    } finally {
      this.setState({ libraryLoading: false });
    }
  };

  toggleLike = async (id) => {
    try {
      const res = await axios.post(`${CDM_LIBRARY}/${id}/like`, {}, { withCredentials: true });
      const { liked, likes } = res.data;

      this.setState((prev) => ({
        likedMap: { ...prev.likedMap, [id]: Boolean(liked) },
        library: prev.library.map((x) =>
          x._id === id ? { ...x, likes: typeof likes === "number" ? likes : (x.likes || 0) } : x
        ),
      }));
    } catch (err) {
      console.error("CDMLibrary/like error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to like.");
    }
  };


  deleteFromPublicLibrary = async (id) => {
    try {
      await axios.delete(`${CDM_LIBRARY}/${id}`, { withCredentials: true });
      this.setState((prev) => ({
        library: prev.library.filter((x) => x._id !== id),
      }));
      showSuccessToast("Deleted from public library.");
    } catch (err) {
      console.error("CDMLibrary/delete error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to delete from public library.");
    }
  };


  copyToCollection = async (id) => {
    try {
      const res = await axios.post(`${CDM_LIBRARY}/${id}/copyToUser`, {}, { withCredentials: true });
      const copied = res.data.item;

      this.setState((prev) => ({
        collection: [copied, ...prev.collection],
        collapsed: { ...prev.collapsed, [copied._id]: true },
        activeTab: "collection",
      }));

      showSuccessToast(res?.data?.message || "Copied to your collection.");
    } catch (err) {
      console.error("CDMLibrary/copyToUser error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to copy.");
    }
  };

  // ---------------------------
  // Render helpers
  // ---------------------------
  getCategoryChip = (category) => {
    const found =
      this.categoryOptions.find((c) => c.label === category) ||
      this.categoryOptions[0];

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm ring-1 ring-black/5 ${found.color}`}
      >
        {category || found.label}
      </span>
    );
  };

  renderReadOnlySections(item) {
    const renderList = (label, arr) => (
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-gradient-to-r from-sky-800 to-cyan-700 px-4 py-3 text-center text-sm font-bold text-white">
          {label}
        </div>

        <div className="p-4">
          <div className="space-y-3">
            {(arr || []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-400">
                —
              </div>
            ) : (
              (arr || []).map((e) => (
                <div
                  key={e._id || `${label}_${e.entry}`}
                  className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm"
                >
                  <div className="text-sm leading-6 text-slate-800">
                    {e.entry}
                  </div>
                  <div className="mt-2 text-[11px] italic text-slate-500">
                    {e.dateAdded ? new Date(e.dateAdded).toLocaleString() : ""}
                    {e.author ? ` · ${e.author}` : ""}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );

    return (
      <div className="mt-5 grid grid-cols-4 gap-4 xs:grid-cols-2 sm:grid-cols-2">
        {renderList("Goals", item.goals)}
        {renderList("Todos", item.todos)}
        {renderList("Referrals", item.referrals)}
        {renderList("Comments", item.comments)}
      </div>
    );
  }

  renderEditableSections(item) {
    const renderList = (label, type, arr) => {
      const key = this.entryKey(item._id, type);
      const items = arr || [];

      return (
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-gradient-to-r from-sky-800 to-cyan-700 px-4 py-3 text-center text-sm font-bold text-white">
            {label}
          </div>

          <div className="p-4">
            <div className="space-y-3">
              {items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-400">
                  —
                </div>
              ) : (
                items.map((e) => {
                  const isEditing =
                    this.state.editingEntries[e._id] !== undefined;

                  return (
                    <div
                      key={e._id}
                      className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm"
                    >
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            className="w-full rounded-2xl border border-slate-300 bg-white p-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                            value={this.state.editingEntries[e._id]}
                            onChange={(ev) =>
                              this.setState((prev) => ({
                                editingEntries: {
                                  ...prev.editingEntries,
                                  [e._id]: ev.target.value,
                                },
                              }))
                            }
                            onKeyDown={(ev) =>
                              this.saveEditedEntry(item._id, type, e._id, ev)
                            }
                          />

                          <div className="flex justify-end">
                            <button
                              className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-200"
                              onClick={() => this.cancelEditEntry(e._id)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <div
                              className="cursor-pointer rounded-2xl bg-slate-50 p-3 transition hover:bg-slate-100"
                              onDoubleClick={() =>
                                this.startEditEntry(e._id, e.entry)
                              }
                            >
                              <div className="text-sm leading-6 text-slate-800">
                                {e.entry}
                              </div>
                              <div className="mt-2 text-[11px] italic text-slate-500">
                                {e.dateAdded
                                  ? new Date(e.dateAdded).toLocaleString()
                                  : ""}
                                {e.author ? ` · ${e.author}` : ""}
                              </div>
                            </div>
                          </div>

                          <button
                            className="mt-1 h-fit rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-100"
                            onClick={() =>
                              this.deleteEntryFromTemplate(item._id, type, e._id)
                            }
                            title="Delete"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <input
              className="mt-4 w-full rounded-2xl border border-slate-300 bg-slate-50 p-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
              placeholder={`Type ${type} and press Enter…`}
              value={this.state.entryInputs[key] || ""}
              onChange={(e) =>
                this.handleEntryInputChange(item._id, type, e.target.value)
              }
              onKeyDown={(e) => this.addEntryToTemplate(item._id, type, e)}
            />
          </div>
        </div>
      );
    };

    return (
      <div className="mt-5 grid grid-cols-4 gap-4 xs:grid-cols-2 sm:grid-cols-2">
        {renderList("Goals", "goal", item.goals)}
        {renderList("Todos", "todo", item.todos)}
        {renderList("Referrals", "referral", item.referrals)}
        {renderList("Comments", "comment", item.comments)}
      </div>
    );
  }

  // ---------------------------
  // Render main sections
  // ---------------------------
 
 renderCollection() {
  const {
    collection,
    collectionLoading,
    searchCollection,
    filterCategory,
    editingId,
    collapsed,
  } = this.state;

  const filtered = (collection || [])
    .filter((x) => {
      const matchesCat =
        filterCategory === "All" || (x.category || "") === filterCategory;
      const q = searchCollection.trim().toLowerCase();
      const matchesQ = !q || (x.title || "").toLowerCase().includes(q);
      return matchesCat && matchesQ;
    })
    .sort(
      (a, b) =>
        new Date(b.updatedAt || b.createdAt || 0) -
        new Date(a.updatedAt || a.createdAt || 0)
    );

  return (
    <div className="mt-6 space-y-6">
      {/* Create new */}
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
        <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-6 py-5 text-white">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-2xl font-bold tracking-tight">
                Create Template
              </div>
              <div className="mt-1 text-sm text-white/80">
                Build a reusable chronic disease management template for your personal collection.
              </div>
            </div>

            <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              Quick Create
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm text-slate-600">
            Start with a title, choose a category, then create it manually or use AI to scaffold it faster.
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-[1.5fr_220px_auto]">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Template title
              </label>
              <input
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                placeholder="Template title (e.g. Diabetes annual review)"
                value={this.state.newTitle}
                onChange={(e) => this.setState({ newTitle: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") this.addToCollection();
                }}
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Category
              </label>
              <select
                value={this.state.selectedCategory}
                onChange={(e) =>
                  this.setState({ selectedCategory: e.target.value })
                }
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
              >
                {this.categoryOptions.map((c) => (
                  <option key={c.label}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2 xl:pt-7">
              <button
                onClick={this.addToCollection}
                className="rounded-2xl bg-gradient-to-r from-sky-700 to-cyan-700 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:from-sky-800 hover:to-cyan-800"
              >
                Add
              </button>

              <button
                onClick={this.addToCollection}
                className="rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
              >
                Add with AI
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm font-semibold text-slate-700">
              Filter & Search
            </div>

            <select
              value={filterCategory}
              onChange={(e) => this.setState({ filterCategory: e.target.value })}
              className="rounded-2xl w-44 border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
            >
              <option>All</option>
              {this.categoryOptions.map((c) => (
                <option key={c.label}>{c.label}</option>
              ))}
            </select>

            <input
              className="w-80 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 xs:w-full sm:w-full"
              placeholder="Search your collection…"
              value={searchCollection}
              onChange={(e) =>
                this.setState({ searchCollection: e.target.value })
              }
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {filtered.length} template{filtered.length === 1 ? "" : "s"}
            </div>

            <button
              onClick={this.loadCollection}
              className="rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {collectionLoading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500 shadow-sm">
          Loading templates…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 py-14 text-center text-slate-500">
          No templates found.
        </div>
      ) : (
        <div className="space-y-5">
          {filtered.map((item) => {
            const isEditing = editingId === item._id;
            const isCollapsed = collapsed[item._id] !== false;

            return (
              <div
                key={item._id}
                className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg"
              >
                <div className="p-5">
                  <div className="flex flex-row gap-4 xl:flex-row xl:items-start xl:justify-between">
                    {/* Left */}
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="flex flex-col gap-3 lg:flex-row">
                          <input
                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-lg font-bold text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                            value={this.state.editingTitle}
                            onChange={(e) =>
                              this.setState({ editingTitle: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                this.updateCollectionItem(item._id);
                              }
                            }}
                          />

                          <select
                            className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                            value={this.state.editingCategory}
                            onChange={(e) =>
                              this.setState({ editingCategory: e.target.value })
                            }
                          >
                            {this.categoryOptions.map((c) => (
                              <option key={c.label}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="text-2xl font-bold text-slate-800">
                              {item.title}
                            </div>
                            {this.getCategoryChip(item.category)}
                            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                              Updated{" "}
                              {new Date(
                                item.updatedAt || item.createdAt || 0
                              ).toLocaleDateString("en-AU")}
                            </div>
                          </div>

                          <div className="mt-2 text-sm text-slate-500">
                            {item.isPublic
                              ? "Published to library"
                              : "Private in your collection"}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Right actions */}
                    <div className="flex flex-wrap items-center gap-2">
                      {!isEditing && (
                        <button
                          onClick={() => this.toggleCollapse(item._id)}
                          className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                        >
                          {isCollapsed ? "Expand" : "Collapse"}
                        </button>
                      )}

                      {isEditing ? (
                        <>
                          <button
                            onClick={() => this.updateCollectionItem(item._id)}
                            className="rounded-2xl bg-gradient-to-r from-sky-700 to-cyan-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-sky-800 hover:to-cyan-800"
                          >
                            Update
                          </button>

                          <button
                            onClick={this.cancelEditTemplate}
                            className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => this.startEditTemplate(item)}
                            className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                          >
                            Edit
                          </button>

                          {item.isPublic ? (
                            <button
                              disabled
                              className="cursor-not-allowed rounded-2xl bg-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600"
                              title="Already published"
                            >
                              Published
                            </button>
                          ) : (
                            <button
                              onClick={() => this.publishCollectionItem(item._id)}
                              className="rounded-2xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-800"
                            >
                              Publish
                            </button>
                          )}

                          <button
                            onClick={() => this.deleteCollectionItem(item._id)}
                            className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="mt-6 border-t border-slate-200 pt-5">
                      {this.renderEditableSections(item)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

  renderPublicLibrary() {
    const { library, libraryLoading, searchLibrary, filterCategory, likedMap } =
      this.state;

    const filtered = (library || [])
      .filter((x) => {
        const matchesCat =
          filterCategory === "All" || (x.category || "") === filterCategory;
        const q = searchLibrary.trim().toLowerCase();
        const matchesQ = !q || (x.title || "").toLowerCase().includes(q);
        return matchesCat && matchesQ;
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    return (
      <div className="mt-6 space-y-5">
        {/* Filters */}
        <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm font-semibold text-slate-700">Filter</div>

            <select
              value={filterCategory}
              onChange={(e) => this.setState({ filterCategory: e.target.value })}
              className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
            >
              <option>All</option>
              {this.categoryOptions.map((c) => (
                <option key={c.label}>{c.label}</option>
              ))}
            </select>

            <input
              className="w-80 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 xs:w-full sm:w-full"
              placeholder="Search public library…"
              value={searchLibrary}
              onChange={(e) => this.setState({ searchLibrary: e.target.value })}
            />

            <button
              onClick={this.loadLibrary}
              className="rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
            >
              Refresh
            </button>
          </div>
        </div>

        {libraryLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 shadow-sm">
            Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((item) => {
              const liked = Boolean(likedMap[item._id]);
              const LikeIcon = liked ? HandThumbUpSolid : HandThumbUpOutline;
              const isCollapsed = this.state.collapsed[item._id] !== false;

              return (
                <div
                  key={item._id}
                  className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg"
                >
                  <div className="p-5">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="text-2xl font-bold text-slate-800">
                          {item.title}
                        </div>
                        {this.getCategoryChip(item.category)}
                        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                          {item.author ? `by ${item.author}` : "Unknown author"}
                        </div>

                        <button
                          onClick={() => this.toggleCollapse(item._id)}
                          className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                        >
                          {isCollapsed ? "Expand" : "Collapse"}
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-2 items-center">
                        {item.author !== this.state.author && (
                          <>
                            <button
                              onClick={() => this.toggleLike(item._id)}
                              className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100"
                              title="Like"
                            >
                              <LikeIcon className="h-5 w-5" />
                              <span>{item.likes || 0}</span>
                            </button>

                            <button
                              onClick={() => this.copyToCollection(item._id)}
                              className="rounded-2xl bg-gradient-to-r from-sky-700 to-cyan-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-sky-800 hover:to-cyan-800"
                              title="Copy to my collection"
                            >
                              Copy
                            </button>
                          </>
                        )}

                        {item.author === this.state.author && (
                          <button
                            onClick={() => this.deleteFromPublicLibrary(item._id)}
                            className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div className="mt-5">
                        {this.renderReadOnlySections(item)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-10 text-center text-slate-500">
                No templates found.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { activeTab } = this.state;

    const tabBase =
      "px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200";
    const tabOn = "bg-white text-sky-700 shadow-sm ring-1 ring-slate-200";
    const tabOff = "text-slate-500 hover:text-slate-800 hover:bg-white/70";

    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40 p-8">
          <div className="mx-auto">
            {/* Hero */}
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <div className="bg-gradient-to-r from-slate-900 via-sky-900 to-cyan-800 px-6 py-10 text-white">
                <div className="mx-auto max-w-4xl text-center">
                  <h1 className="text-5xl font-bold tracking-tight text-white xs:text-3xl sm:text-3xl">
                    Chronic Disease Management Library
                  </h1>
                  <p className="mt-3 text-sm text-white/80 sm:text-base">
                    Build your own template collection or explore the shared community library.
                  </p>

                  {/* Tabs */}
                  <div className="mt-6 flex justify-center">
                    <div className="inline-flex items-center gap-1 rounded-2xl bg-white/10 p-1 backdrop-blur-sm">
                      <button
                        className={`${tabBase} ${
                          activeTab === "collection" ? tabOn : tabOff
                        }`}
                        onClick={() => this.setTab("collection")}
                      >
                        Collection
                      </button>
                      <button
                        className={`${tabBase} ${
                          activeTab === "library" ? tabOn : tabOff
                        }`}
                        onClick={() => this.setTab("library")}
                      >
                        Library
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {activeTab === "collection"
                  ? this.renderCollection()
                  : this.renderPublicLibrary()}
              </div>
            </div>

            <ToastContainer />
          </div>
        </div>
      </>
    );
  }
}

const CDMLibraryWithNavigation = (props) => {
  const auth = useAuth();
  const navigate = useNavigation();
  return <CDMLibrary {...props} auth={auth} navigate={navigate} />;
};

export default CDMLibraryWithNavigation;
